import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Sun, Moon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import LanguageToggle from '../../components/common/LanguageToggle';

const PrivacyPolicyPage = () => {
  const { darkMode, toggleTheme } = useTheme();
  const { t } = useTranslation();

  const bg = darkMode ? 'bg-[#0f0f14]' : 'bg-gray-50';
  const textColor = darkMode ? 'text-white/70' : 'text-gray-600';
  const headingColor = darkMode ? 'text-white' : 'text-gray-900';
  const cardBg = darkMode ? 'border-amber-500/[0.12] bg-white/[0.04] backdrop-blur-sm' : 'border-gray-200/70 bg-white shadow-xl';
  const accentText = darkMode ? 'text-amber-400' : 'text-amber-600';

  return (
    <div className={`min-h-screen ${bg} py-12 px-4 transition-colors duration-500`}>
      <div className={`absolute inset-0 transition-colors duration-500 ${darkMode ? 'bg-gradient-to-br from-[#0f0f14] via-[#1a1a24] to-[#2a1f0a]/70' : 'bg-gradient-to-br from-gray-50 via-white to-amber-50/50'}`} />

      <div className="relative z-10 max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-10">
          <Link to="/register" className={`inline-flex items-center gap-2 ${accentText} hover:underline font-semibold`}>
            <ArrowLeft size={18} />
            {t('pages.legal.backLink')}
          </Link>

          <div className="flex items-center gap-2">
            <LanguageToggle className="rounded-lg" />
            <button
              onClick={toggleTheme}
              className={`rounded-lg p-2.5 transition-all duration-300 ${
                darkMode
                  ? 'text-white/60 hover:text-white hover:bg-white/10'
                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
              }`}
              aria-label={t('auth.login.toggleThemeAria')}
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          </div>
        </div>
        
        <div className={`rounded-3xl border p-10 md:p-14 shadow-2xl transition-colors duration-500 ${cardBg}`}>
          <div className="flex items-center gap-4 mb-10">
            <div className={`p-4 rounded-2xl ${darkMode ? 'bg-amber-500/20 text-amber-400 border border-amber-500/20' : 'bg-amber-50 text-amber-600 border border-amber-100'}`}>
              <Shield size={36} />
            </div>
            <div>
              <h1 className={`text-3xl font-bold ${headingColor}`}>{t('pages.legal.privacyTitle')}</h1>
              <p className={`text-sm mt-1 ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>{t('pages.legal.lastUpdated')}</p>
            </div>
          </div>

          <div className={`space-y-10 ${textColor} text-lg leading-relaxed`}>
            <section>
              <h2 className={`text-xl font-bold mb-4 uppercase tracking-wider ${darkMode ? 'text-amber-500/80' : 'text-amber-700/80'}`}>{t('pages.legal.privacyS1h')}</h2>
              <p>{t('pages.legal.privacyS1p')}</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-4 uppercase tracking-wider ${darkMode ? 'text-amber-500/80' : 'text-amber-700/80'}`}>{t('pages.legal.privacyS2h')}</h2>
              <p>{t('pages.legal.privacyS2p')}</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-4 uppercase tracking-wider ${darkMode ? 'text-amber-500/80' : 'text-amber-700/80'}`}>{t('pages.legal.privacyS3h')}</h2>
              <p>{t('pages.legal.privacyS3p')}</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-4 uppercase tracking-wider ${darkMode ? 'text-amber-500/80' : 'text-amber-700/80'}`}>{t('pages.legal.privacyS4h')}</h2>
              <p>{t('pages.legal.privacyS4p')}</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
